import cn from 'classnames';

import { BetActionErrorResponseDataDto } from 'api/common.generated';
import { InfoCircleIcon } from 'assets/icons';
import { BetslipErrors, OutcomeOddsUpdateState } from 'features/coupon/interface';
import { useTranslation } from 'react-i18next';
import { PlaceBet } from 'shared/interface';
import './styles.scss';

interface BetslipCouponWarningProps {
	bet: PlaceBet;
	warnings: string[];
	isEventCompleted: () => boolean;
	isBetUnavailable: boolean | undefined;
	betActionErrors: BetActionErrorResponseDataDto[];
}

const BetslipCouponWarning = ({ bet, betActionErrors, warnings, isEventCompleted, isBetUnavailable }: BetslipCouponWarningProps) => {
	const [t] = useTranslation();

	const isShowOddsUpdateWarning =
		warnings.includes(String(bet.outcomeId)) ||
		bet.outcomeUpdateState === OutcomeOddsUpdateState.ODDS_INCREASED ||
		bet.outcomeUpdateState === OutcomeOddsUpdateState.ODDS_DECREASED;

	const isShowSlipErrorWarning = () => {
		const errorDetails = betActionErrors.flatMap((err) =>
			String((err?.errorDetails?.conflictedCategoryIds && err?.errorDetails?.eventId) || err.referenceObject).split(','),
		);

		return (
			errorDetails.includes(String(bet.gameId)) ||
			errorDetails.includes(String(bet.gameType)) ||
			errorDetails.includes(String(bet.eventId)) ||
			errorDetails.includes(String(bet.eventType)) ||
			errorDetails.includes(String(bet.categoryId))
		);
	};

	const showSlipErrors = isShowOddsUpdateWarning || isShowSlipErrorWarning() || isBetUnavailable;
	const isShowWarning = isShowOddsUpdateWarning && !isBetUnavailable && !isShowSlipErrorWarning();

	const getWarningMsg = () => {
		if (isBetUnavailable || isEventCompleted()) {
			return t('unAvailableBet');
		}

		if (isShowSlipErrorWarning()) {
			const checkError = (errorToCheck: number) => betActionErrors.some((err) => err.betSlipErrorCode === errorToCheck);
			if (checkError(BetslipErrors.CANNOT_COMBINE_CATEGORY)) {
				return t('slipErrorWarning98');
			}
			if (checkError(BetslipErrors.GAME_NOT_COMBINABLE)) {
				return t('slipErrorWarning5');
			}
			if (checkError(BetslipErrors.EVENT_CANNOT_BE_COMBINED_WITH_SELECTION)) {
				return t('slipErrorWarning33');
			}
			if (checkError(BetslipErrors.IDS_CANNOT_BE_COMBINED_WITHIN_SET)) {
				return t('slipErrorWarning75');
			}
			if (checkError(BetslipErrors.OFFER_EVENT_START_AFTER_BONUS_EXPIRE_TIME)) {
				return t('slipErrorWarning206');
			}
		}
		return t('oddsEventChanged');
	};

	if (!showSlipErrors) {
		return null;
	}

	return (
		<div className="betslipCouponWarning">
			<div
				className={cn('betslipCouponWarning__warning', {
					'betslipCouponWarning__warning--isBetUnavailable': isBetUnavailable || isEventCompleted(),
					'betslipCouponWarning__warning--isWarning': isShowSlipErrorWarning() || isShowWarning,
				})}
			>
				<InfoCircleIcon width="16px" height="16px" />
				<p>{getWarningMsg()}</p>
			</div>
		</div>
	);
};

export default BetslipCouponWarning;
